import { action, computed, makeAutoObservable, observable } from "mobx";

// ────────────────────────────────────────────
// SidebarStore
//
// Pure sidebar UI state.  Knows nothing about column configs, multi-
// column selection, or the grid — it just tracks whether the sidebar
// is open and which field key it's pointing at.
//
// Owned by the root ColumnConfigStore and exposed as `store.sidebar`.
// ────────────────────────────────────────────

export class SidebarStore {
  /** The field key of the column currently shown in the sidebar (`null` = none). */
  selectedField: string | null = null;

  /** Whether the sidebar panel is open. */
  isOpen = false;

  constructor() {
    makeAutoObservable(this, {
      selectedField: observable,
      isOpen: observable,
      open: action,
      close: action,
      clearField: action,
      setField: action,
      reset: action,
      hasSelectedField: computed,
    });
  }

  // ── Mutations ─────────────────────────────

  /**
   * Open the sidebar for `field`.
   *
   * Sets both `selectedField` and `isOpen` in a single action so MobX
   * batches the two observable writes into one reaction cycle.
   */
  open = (field: string): void => {
    this.selectedField = field;
    this.isOpen = true;
  };

  /**
   * Close the sidebar panel.
   *
   * `selectedField` is intentionally **not** cleared here so that the
   * sidebar content stays mounted during the close animation.  Call
   * `clearField()` after the transition finishes.
   */
  close = (): void => {
    this.isOpen = false;
  };

  /**
   * Null out the selected field.
   *
   * Typically called after the close animation completes so that the
   * heavy sidebar content can be fully unmounted.
   */
  clearField = (): void => {
    this.selectedField = null;
  };

  /**
   * Set the selected field without changing the open/close state.
   *
   * Useful when you want to swap which column the sidebar points at
   * while it's already open.
   */
  setField = (field: string): void => {
    this.selectedField = field;
  };

  /** Full reset — close and clear in one shot. */
  reset = (): void => {
    this.selectedField = null;
    this.isOpen = false;
  };

  // ── Queries ───────────────────────────────

  /** Whether a field is currently selected (regardless of open state). */
  get hasSelectedField(): boolean {
    return this.selectedField !== null;
  }
}
